/**
 * Barra superior da home no computador (≥1024px) — busca + sacola + conta.
 *
 * No celular a busca é uma lupa no cabeçalho e sacola/conta ficam na barra
 * inferior fixa. No computador aquela barra inferior some (`lg:hidden` na
 * `BottomNavIsland`): centralizada numa tela larga ela flutuava POR CIMA da
 * grade de produtos. As ações mudam pra cá, que é onde o olho procura num
 * cardápio aberto no PC.
 *
 * A busca abre a MESMA `GlobalSearchIsland` do celular — não existe um
 * segundo mecanismo de busca pra divergir do primeiro.
 */
import { useSyncExternalStore } from 'react';
import type { CartLine } from '../types';
import { useI18n } from '../i18n';

function subscribeCart(cb: () => void) {
  window.addEventListener('aliv:cart-updated', cb);
  return () => window.removeEventListener('aliv:cart-updated', cb);
}

// `useSyncExternalStore` exige snapshot estável enquanto o dado não muda —
// devolver um objeto novo a cada chamada provoca re-render infinito.
let ultimo = { count: -1, total: -1 };
function snapshot() {
  const cart: CartLine[] = window.CART || [];
  let count = 0;
  let total = 0;
  for (const it of cart) {
    const q = Number(it.quantity) || 0;
    count += q;
    total += (Number(it.price) || 0) * q;
  }
  if (count !== ultimo.count || total !== ultimo.total) ultimo = { count, total };
  return ultimo;
}

export function DesktopTopBar() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const { t } = useI18n();
  const cart = useSyncExternalStore(subscribeCart, snapshot, () => ultimo);
  const temItens = cart.count > 0;

  return (
    <div className="hidden lg:flex items-center gap-3 sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-100 px-6 py-3">
      <button
        type="button"
        onClick={() => window.openGlobalSearch?.()}
        className="flex-1 min-w-0 flex items-center gap-3 bg-gray-100 hover:bg-gray-200/70 rounded-full px-4 py-2.5 text-left transition"
      >
        <i className="fas fa-search text-gray-400 text-sm" />
        <span className="text-sm font-medium text-gray-400 truncate">{t('O que você procura?')}</span>
      </button>

      <button
        type="button"
        onClick={() => window.shareApp?.()}
        aria-label={t('Compartilhar')}
        className="w-10 h-10 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition shrink-0"
      >
        <i className="fas fa-share-alt" />
      </button>

      <button
        type="button"
        onClick={() => window.openUserArea?.('orders')}
        className="h-10 px-4 flex items-center gap-2 rounded-full text-gray-600 hover:bg-gray-100 transition shrink-0 text-xs font-bold"
      >
        <i className="fas fa-user text-gray-400" /> {t('Conta')}
      </button>

      <button
        type="button"
        onClick={() => window.openCartModal?.()}
        className={[
          'h-10 pl-4 pr-3 flex items-center gap-2.5 rounded-full shrink-0 text-xs font-bold transition',
          temItens
            ? 'bg-red-600 text-white hover:bg-red-700 shadow-sm'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200/70',
        ].join(' ')}
      >
        <i className="fas fa-shopping-bag" />
        {t('Sacola')}
        {temItens && (
          <span className="bg-white/25 rounded-full px-2 py-0.5 tabular-nums">
            {cart.count}
          </span>
        )}
      </button>
    </div>
  );
}
